import { 
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signOut,
    sendPasswordResetEmail,
    GoogleAuthProvider,
    OAuthProvider,
    signInWithPopup,
    sendEmailVerification,
    updateProfile
} from "firebase/auth";
import { auth, db } from "../services/firebase";
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import { adminAuditService } from "../services/adminAuditService";
import { agentService } from "../services/agentService";

// Utility to remove undefined fields from an object
function removeUndefined(obj) {
    return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v !== undefined));
}

// Helper function to ensure user profile exists
async function ensureUserProfile(user) {
    if (!user) return;
  
    const userRef = doc(db, "users", user.uid);
    const userSnap = await getDoc(userRef);
  
    if (!userSnap.exists()) {
        await setDoc(userRef, removeUndefined({
            email: user.email,
            displayName: user.displayName,
            status: "pending_kyc",
            profileCompleted: false,
            createdAt: new Date().toISOString(),
            isAdmin: false,
            role: "user",
            balance: 0
        }));
    } else {
    // If user exists but has no balance, set it to 0
        const data = userSnap.data();
        if (typeof data.balance !== "number") {
            await setDoc(userRef, removeUndefined({ balance: 0 }), { merge: true });
        }
    }
}

export const authService = {
    async signup(email, password) {
        const cred = await createUserWithEmailAndPassword(auth, email, password);
        await ensureUserProfile(cred.user);
        return cred;
    },

    async login(email, password) {
        const cred = await signInWithEmailAndPassword(auth, email, password);
        await ensureUserProfile(cred.user);
        // Update lastLogin in Firestore
        const userRef = doc(db, "users", cred.user.uid);
        await updateDoc(userRef, { lastLogin: new Date().toISOString() });
        return cred;
    },

    async loginWithGoogle() {
        const provider = new GoogleAuthProvider();
        
        const cred = await signInWithPopup(auth, provider);
        
        await ensureUserProfile(cred.user);
        
        // Update lastLogin in Firestore
        const userRef = doc(db, "users", cred.user.uid);
        await updateDoc(userRef, { lastLogin: new Date().toISOString() });
        
        return cred;
    },

    async loginWithMicrosoft() {
        const provider = new OAuthProvider("microsoft.com");
        
        const cred = await signInWithPopup(auth, provider);
        
        await ensureUserProfile(cred.user);
        
        // Update lastLogin in Firestore
        const userRef = doc(db, "users", cred.user.uid);
        await updateDoc(userRef, { lastLogin: new Date().toISOString() });
        
        return cred;
    },

    logout() {
        return signOut(auth);
    },

    resetPassword(email) {
        return sendPasswordResetEmail(auth, email);
    },

    ensureUserProfile,
    
    async signupWithEmailVerification(email, password, name) {
        const userCred = await createUserWithEmailAndPassword(auth, email, password);
        await updateProfile(userCred.user, { displayName: name });
        await sendEmailVerification(userCred.user);
        
        // Create user document with new unified onboarding structure
        await setDoc(doc(db, "users", userCred.user.uid), removeUndefined({
            email,
            firstName: name.split(" ")[0] || name,
            lastName: name.split(" ").slice(1).join(" ") || "",
            status: "pending_kyc",
            profileCompleted: false,
            createdAt: new Date().toISOString(),
            isAdmin: false,
            role: "INVESTOR", // Default role, will be updated based on user preferences
            
            // New unified onboarding fields
            roles: {
                investor: false,
                agent: false,
                admin: false
            },
            wantsToInvest: false,
            wantsToPublish: false,
            affiliations: {
                agencyId: null,
                isAgencyOwner: false
            },
            
            // Financial defaults
            balance: 0,
            totalInvestment: 0,
            totalInvested: 0,
            investmentCount: 0,
            
            // Profile defaults
            tier: "Newcomer",
            badges: [],
            kycStatus: "not_started",
            clientType: "individual",
            offboardingStatus: "none",
            
            // Timestamps
            updatedAt: new Date().toISOString(),
            lastLoginAt: new Date().toISOString()
        }));
        
        return userCred;
    },
    
    // New unified register method for UnifiedSignup component
    async register(email, password, userData = {}) {
        const userCred = await createUserWithEmailAndPassword(auth, email, password);
        
        // Update profile with display name if provided
        if (userData.firstName || userData.lastName) {
            const displayName = `${userData.firstName || ""} ${userData.lastName || ""}`.trim();
            if (displayName) {
                await updateProfile(userCred.user, { displayName });
            }
        }
        
        // Send email verification
        await sendEmailVerification(userCred.user);
        
        // Create comprehensive user document with unified onboarding structure
        await setDoc(doc(db, "users", userCred.user.uid), removeUndefined({
            email,
            firstName: userData.firstName || "",
            lastName: userData.lastName || "",
            phone: userData.phone || "",
            status: "pending_kyc",
            profileCompleted: false,
            createdAt: new Date().toISOString(),
            isAdmin: false,
            role: userData.userType || "INVESTOR", // Use provided userType or default to INVESTOR
            
            // Unified onboarding fields
            roles: {
                investor: userData.userType === "INVESTOR" || false,
                agent: userData.userType === "AGENT" || false,
                admin: false
            },
            wantsToInvest: userData.userType === "INVESTOR" || false,
            wantsToPublish: userData.userType === "AGENT" || false,
            affiliations: {
                agencyId: null,
                isAgencyOwner: false
            },
            
            // Financial defaults
            balance: 0,
            totalInvestment: 0,
            totalInvested: 0,
            investmentCount: 0,
            
            // Profile defaults
            tier: "Newcomer",
            badges: [],
            kycStatus: "not_started",
            clientType: "individual",
            offboardingStatus: "none",
            
            // Marketing preferences
            acceptMarketing: userData.acceptMarketing || false,
            
            // Timestamps
            updatedAt: new Date().toISOString(),
            lastLoginAt: new Date().toISOString()
        }));
        
        // Log successful registration
        await adminAuditService.logUserManagementAction(userCred.user.uid, "user_registered", userCred.user.uid, {
            email,
            userType: userData.userType,
            acceptMarketing: userData.acceptMarketing,
            registrationMethod: "unified_signup"
        });
        
        return userCred;
    },
    
    // New method to update user role preferences
    async updateRolePreferences(userId, preferences) {
        const userRef = doc(db, "users", userId);
        await updateDoc(userRef, {
            wantsToInvest: preferences.wantsToInvest || false,
            wantsToPublish: preferences.wantsToPublish || false,
            "roles.investor": preferences.wantsToInvest || false,
            "roles.agent": preferences.wantsToPublish || false,
            updatedAt: new Date().toISOString()
        });
        
        // Log the role preference update
        await adminAuditService.logUserManagementAction(userId, "update_role_preferences", userId, {
            oldPreferences: {},
            newPreferences: preferences
        });
    },
    
    // New method to activate agent role
    async activateAgentRole(userId, agentData) {
        const userRef = doc(db, "users", userId);
        
        // Update user roles
        await updateDoc(userRef, {
            "roles.agent": true,
            role: "AGENT", // Set primary role to AGENT
            updatedAt: new Date().toISOString()
        });
        
        // Create agent document
        const agentId = await agentService.createAgent(agentData, userId);
        
        // Log the agent activation
        await adminAuditService.logUserManagementAction(userId, "activate_agent_role", userId, {
            agentId: agentId,
            agentData: agentData
        });
        
        return agentId;
    }
}; 